import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSetAtom } from 'jotai';
import { openSnackbarAdviseAtom } from '../../Atoms';
import useAPI from '../../api';
import { FullPageFlexBox, FullSizeColumn } from '../../Components/FlexBox';
import {
  formatPayloadDate,
  getThisMonthRange,
  getMonthRange,
} from '../../Helpers/dates';
import FiltersNavbar from './Components/FiltersNavbar';
import GraphsWrapper from './Components/GraphsWrapper';
import PivotReportWrapper from './Components/PivotReportWrapper';

const payloadPeriod = function payloadPeriod(per = 'month') {
  let returnRange = [];
  if (typeof per === 'number') {
    returnRange = getMonthRange(per);
  } else if (per === 'month') {
    returnRange = getThisMonthRange();
  }

  return returnRange;
};

const RedemptionWrapper = function RedemptionWrapper({
  userRoles,
  isRolling = false,
}) {
  const API = useAPI();
  const intl = useIntl();
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  const isOperator = Boolean(
    userRoles?.find(
      ({ roleId }) => roleId === 'OPERATOR' || roleId === 'LIMITEDOPERATOR'
    )
  );

  const [chartView, setChartView] = useState(
    isOperator
      ? true
      : localStorage.getItem('outboundPivotChartView') === 'true'
  );
  const [navbarOpen, setNavbarOpen] = useState(true);
  const [currentFilters, setCurrentFilters] = useState({ periodName: 'month' });
  const [branchData, setBranchData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [minimumRedemptionCalls, setMinimumRedemptionCalls] = useState(null);

  const handleToggleNavbar = () => {
    setNavbarOpen(!navbarOpen);
  };

  const redemptionCallback = (entityType, start, end) =>
    API.getRedemption(
      formatPayloadDate(start)?.toISOString(),
      formatPayloadDate(end)?.toISOString(),
      entityType, //entityType (BRANCH or USER)
      { silent: currentFilters?.autoRefresh },
      (res) => {
        if (entityType === 'BRANCH') {
          setBranchData(res);
        } else {
          setUserData(res);
        }
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.ERROR_REPORT_LOADING,
          severity: 'error',
        });
      }
    );

  const rollingRedemptionCallback = (entityType) =>
    API.getRollingRedemption(
      formatPayloadDate(new Date())?.toISOString(),
      entityType, //entityType (BRANCH or USER)
      { silent: currentFilters?.autoRefresh },
      (res) => {
        if (entityType === 'BRANCH') {
          setBranchData(res);
        } else {
          setUserData(res);
        }
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.ERROR_REPORT_LOADING,
          severity: 'error',
        });
      }
    );

  useEffect(() => {
    if (!currentFilters) return;

    const [start, end] = payloadPeriod(currentFilters?.periodName);

    if (isRolling) {
      rollingRedemptionCallback('BRANCH', start);
    } else {
      redemptionCallback('BRANCH', start, end);
    }
  }, [currentFilters]);

  useEffect(() => {
    if (!currentFilters) return;

    const [start, end] = payloadPeriod(currentFilters?.periodName);

    if (isRolling) {
      rollingRedemptionCallback('USER', start);
    } else {
      redemptionCallback('USER', start, end);
    }
  }, [currentFilters]);

  useEffect(() => {
    if (!isRolling) return;

    API.getSettingValue(
      'MINIMUM_REDEMPTION_CALLS',
      (res) => {
        setMinimumRedemptionCalls(Number(res));
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.ERROR_LOADING_MINIMUM_REDEMPTION_CALLS,
          severity: 'error',
        });
      }
    );
  }, [isRolling]);

  const toggleChartView = () => {
    setChartView(!chartView);
    localStorage.setItem('outboundPivotChartView', JSON.stringify(!chartView));
  };

  return (
    <FullPageFlexBox flexDirection="column" overflow="hidden">
      <FiltersNavbar
        toggle={handleToggleNavbar}
        open={navbarOpen}
        currentFilters={currentFilters}
        setCurrentFilters={setCurrentFilters}
        switchValue={chartView}
        switchLabel={
          !chartView ? intl.messages.PIVOT_VIEW : intl.messages.CHART_VIEW
        }
        onSwitchClick={isOperator ? undefined : toggleChartView}
        isRolling={isRolling}
        hideExport2Excel={isOperator}
        isOperator={isOperator}
      />
      <FullSizeColumn overflow="auto" padding="24px">
        <FullSizeColumn
          display={!chartView ? 'none!important' : 'inherit!important'}
          gap="24px"
        >
          <GraphsWrapper
            userRedemptionData={userData?.redemptionData}
            branchRedemptionData={branchData?.redemptionData}
            isRolling={isRolling}
            isOperator={isOperator}
            minimumRedemptionCalls={minimumRedemptionCalls}
          />
        </FullSizeColumn>
        <FullSizeColumn
          display={!chartView ? 'flex!important' : 'none!important'}
        >
          <PivotReportWrapper
            branchRedemptionData={branchData?.redemptionData}
            userRedemptionData={userData?.redemptionData}
            branchPivotData={branchData?.practicePivotData}
            userPivotData={userData?.practicePivotData}
            isOperator={isOperator}
            userRoles={userRoles}
            chartView={chartView}
            isRolling={isRolling}
          />
        </FullSizeColumn>
      </FullSizeColumn>
    </FullPageFlexBox>
  );
};

export default RedemptionWrapper;
